import './PromoPost.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'react-router5';

import { PromoPostProps } from '$shared/components/PromoPost/PromoPost.interface';
import { Block, Button } from '$uikit';
import { Attachments, Truncate, UnitName, UnitPhoto, Verified, Viewer } from '$shared/components';
import { useDesktop, useTranslator, useUserInfo, useVisibleDuration } from '$hooks';
import { IcMessage28, IcUserPlus28 } from '$assets';
import { CHAT, PROFILE, RESOLVE } from '$shared/constants/pages';
import { followersActions, followersSelector } from '$store/followers';
import { mainActions, mainSelector } from '$store/main';
import { ViewerItem } from '$shared/components/Viewer/VieverItem/ViewerItem.interface';
import { adsActions } from '$store/ads';
import { classNames } from '$utils';

export const PromoPost: FC<PromoPostProps> = (props) => {
  const { post, adId } = props;

  const { decryptKeys } = useSelector(mainSelector);
  const dispatch = useDispatch();
  const { following } = useSelector(followersSelector);
  const router = useRouter();
  const user = useUserInfo(post.ownerId);
  const isFollowed = following[post.ownerId];
  const postRef = useRef<HTMLDivElement>(null);
  const t = useTranslator();

  useVisibleDuration(postRef, () => {
    if (adId > 0) {
      dispatch(adsActions.sendView(adId));
    }
  });

  useEffect(() => {
    return () => {
      if (adId > 0) {
        dispatch(adsActions.sendView(adId));
      }
    };
  }, [adId]);

  const handleChat = useCallback(() => {
    router.navigate(CHAT, { peerId: post.ownerId });
  }, [post]);

  const handleFollow = useCallback(() => {
    dispatch(
      followersActions.followFromAds({
        toId: post.ownerId,
        adsId: adId,
      }),
    );
  }, [post, adId]);

  const handleProfile = useCallback(() => {
    if (user.domain) {
      router.navigate(RESOLVE, { domain: user.domain });
    } else {
      router.navigate(PROFILE, { id: user.id });
    }
  }, [user]);

  const viewerItems = useMemo(() => {
    if (post.attachments.length > 0 && post.attachments[0].type === 'video') {
      return [];
    }

    const items: ViewerItem[] = [];
    for (let i = 0; i < post.attachments.length; i++) {
      const item = post.attachments[i];
      if (item.type === 'video') {
        items.push({
          id: -item.video!.videoId,
          source: item.video!.photoLarge,
          width: item.video!.width,
          height: item.video!.height,
        });
      } else if (item.type === 'photo') {
        items.push({
          id: item.photo!.photoId,
          source: item.photo!.photoLarge,
          width: item.photo!.width,
          height: item.photo!.height,
        });
      }
    }

    return items;
  }, []);

  const handleClick = useCallback(
    (e) => {
      const target = e.target;

      if (target.closest('.Collage__item')) {
        e.stopPropagation();

        const el = target.closest('.Collage__item');
        const id = el.dataset.id * (el.classList.contains('video') ? -1 : 1);
        let sel = 0;
        for (let i = 0; i < viewerItems.length; i++) {
          if (viewerItems[i].id === id) {
            sel = i;
            break;
          }
        }

        dispatch(
          mainActions.setPopup(
            <PhotoView viewerItems={viewerItems} selectedIndex={sel} />,
          ),
        );
        return;
      }
    },
    [viewerItems],
  );

  const textOptions = useMemo(() => {
    if (post.attachments && post.attachments.length) {
      let hasMedia = false;
      for (let attach of post.attachments) {
        if (attach.type === 'photo' || attach.type === 'video') {
          hasMedia = true;
          break;
        }
      }

      if (hasMedia) {
        return {
          lines: 6,
          limit: 180,
          shortFrom: 126,
        };
      }
    }

    return {
      lines: 12,
      limit: 300,
      shortFrom: 250,
    };
  }, [post.attachments]);

  return (
    <Block className="PromoPost">
      <div ref={postRef}>
        <div className="PromoPost__head">
          <UnitPhoto unit={user} size="small" />
          <div className="PromoPost__head__info" onClick={handleProfile}>
            <div className="PromoPost__head__name_wrap">
              <div className="PromoPost__head__name">
                <UnitName unit={user} />
              </div>
              <Verified objectId={user.id} />
            </div>
            <div className="PromoPost__sponsored">{t('post_sponsored')}</div>
          </div>
        </div>
        <div onClick={handleClick}>
          <Attachments
            parentObject={`post_ads_${post.id}`}
            decryptKey=""
            attachments={post.attachments}
          />
        </div>
        <div className="PromoPost__content">
          <div className="PromoPost__text">
            {post.text.length > 0 && (
              <div
                className={classNames({
                  Post__text: true,
                  short: post.text.length <= textOptions.shortFrom,
                })}
              >
                <Truncate
                  lines={textOptions.lines}
                  cut={false}
                  limit={textOptions.limit}
                  text={post.text}
                  expandText={t('post_text_expand')}
                  adId={adId}
                />
              </div>
            )}
          </div>
          <div className="PromoPost__actions">
            {!isFollowed && (
              <Button iconRight={<IcUserPlus28 />} onClick={handleFollow}>
                {t('promo_post_follow')}
              </Button>
            )}
            {(isFollowed || decryptKeys[-1]) && (
              <Button
                iconRight={<IcMessage28 />}
                mode={isFollowed ? 'default' : 'quinary'}
                onClick={handleChat}
              >
                {t('promo_post_message')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Block>
  );
};

const PhotoView: FC<{ viewerItems: ViewerItem[]; selectedIndex: number }> = ({
  viewerItems,
  selectedIndex,
}) => {
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const [currentViewerItem, setCurrentViewerItem] = useState(selectedIndex);

  const handleCloseViewer = useCallback(() => {
    dispatch(mainActions.setPopup(null));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseViewer();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentViewerItem]);

  return (
    <Viewer
      items={viewerItems}
      active={currentViewerItem}
      onChange={setCurrentViewerItem}
      onClose={handleCloseViewer}
      mobile={!isDesktop}
    />
  );
};
